// ─────────────────────────────────────────────────────────────────────────────
// makeTimeFormatter — the ONE clock.
//
// Moved here from components/dashboard/PulseSchedulePanel.tsx (owner ruling
// 2026-07-31, "one core, every window"): it is a pure data utility that four
// modules and the world-clock regression already imported out of a render file.
// PulseSchedulePanel re-exports it unchanged, so every existing import and
// PulseScheduleWorldClock.test.tsx keep resolving to this exact implementation.
//
// BODY IS BYTE-IDENTICAL to the version it replaces. Do not "tidy" it: the
// fallback branch below is load-bearing (see the comment inside).
// ─────────────────────────────────────────────────────────────────────────────

// Every label is formatted by a formatter built from the zone the PAYLOAD
// carries — never the browser's zone.
export function makeTimeFormatter(
  tz: string | undefined | null,
  withSeconds = false,
): (iso: string) => string {
  let intl: Intl.DateTimeFormat | null = null;
  if (typeof tz === 'string' && tz.trim()) {
    try {
      intl = new Intl.DateTimeFormat('en-GB', {
        timeZone: tz.trim(),
        hour: '2-digit',
        minute: '2-digit',
        ...(withSeconds ? { second: '2-digit' as const } : {}),
        hour12: false,
      });
    } catch (e) {
      // Intl throws RangeError for an unknown zone — an older backend or a bad
      // stored value. Fall through to the offset-local fallback below.
      intl = null;
    }
  }
  return (iso: string): string => {
    const blank = withSeconds ? '--:--:--' : '--:--';
    if (typeof iso !== 'string' || !iso) return blank;
    if (intl) {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return blank;
      return intl.format(d);
    }
    // FALLBACK — the ISO carries its own offset, so its wall time IS the event's
    // own recorded local hour. The device's zone is NEVER the fallback: rendering
    // in the browser's clock is the exact defect this formatter exists to close.
    const wall = iso.slice(11, withSeconds ? 19 : 16);
    return /^\d{2}:\d{2}/.test(wall) ? wall : blank;
  };
}
